import { staffNotifyEvent } from '@planuze/admin-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/* Mesma origem do app (Vite proxy `/v1` com ws:true em dev; gateway em prod). O cookie
   HttpOnly de staff vai no handshake automaticamente (§4.9). */
function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/v1/admin/notify/ws`;
}

/* Realtime do backoffice de staff (TASK-PUB-50): escuta o `StaffNotifyDO` (sala global) e, a
   cada `queue_changed` (nova submissão OU decisão de qualquer staff), invalida a fila de
   moderação → o admin-web refetcha sem ctrl+r. Reconnect exponencial (1s→30s, máx. 5) e
   cleanup em unmount (§4.5). Unidirecional: não envia frames. */
export function useStaffNotify(): { connectionState: ConnectionState } {
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const attempts = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);

  const connect = useCallback(() => {
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;
    ws.onopen = () => {
      attempts.current = 0;
      setConnectionState('open');
    };
    ws.onmessage = (event: MessageEvent) => {
      let raw: unknown;
      try {
        raw = JSON.parse(typeof event.data === 'string' ? event.data : '');
      } catch {
        return;
      }
      const parsed = staffNotifyEvent.safeParse(raw);
      if (!parsed.success) return;
      /* `queue_changed`: a verdade é o servidor → invalida e refetcha (não confia no payload). */
      queryClient
        .invalidateQueries({ queryKey: ['pack-submissions', 'list'] })
        .catch(() => undefined);
    };
    ws.onclose = () => {
      if (closedByUs.current) return;
      if (attempts.current >= 5) {
        setConnectionState('closed');
        return;
      }
      setConnectionState('reconnecting');
      const delay = Math.min(1000 * 2 ** attempts.current, 30_000);
      attempts.current += 1;
      timerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
  }, [queryClient]);

  useEffect(() => {
    closedByUs.current = false;
    attempts.current = 0;
    connect();
    return () => {
      closedByUs.current = true;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      wsRef.current?.close(1000, 'unmount');
      wsRef.current = null;
    };
  }, [connect]);

  return { connectionState };
}
